import { AgenticLoaderPreview } from '../../../../../interfaces/chat';
import { appState } from '../../../../../interfaces/common';
import { Button, Card, Flex, Icon, Image, useElement, Tooltip } from '@pega/cosmos-react-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import useLocalTranslation from '../../../../../hooks/useLocalTranslation';
import { IconButtonStyle, lightGray, StyledCardPreview, StyledDimension } from '../../../../Utilities/custom.styled';
import ChatHeader from './ChatHeader';
import ChatToggle from './ChatToggle';
import Conversation from './Conversation';
import ConversationInput from './ConversationInput';
import Footer from './Footer';
import MultiUserExperience from './MultiUserExperience';

interface ChatContainerProps {
  selectedColors: any;
  isMultiExpExpanded: boolean;
  setIsMultiExpExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  agenticPreview?: AgenticLoaderPreview | null;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  selectedColors,
  isMultiExpExpanded,
  setIsMultiExpExpanded,
  agenticPreview,
}) => {
  const {
    chatInfo: {
      footerText,
      colorTheme,
      headerTextColor,
      widgetPosition,
      widgetButtonType,
      textOrientationType,
      hideChatLauncher,
      fontSet,
      selectedFontId,
      logo,
      displayLogo,
      enableKM,
      enableCases,
      enableMultiExperienceWidget,
      widgetDimension,
    },
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
    };
  });
  const [customFontFamily, setCustomFontFamily] = useState('');
  const selectedThemeColor = selectedColors?.selectedColorTheme || colorTheme;
  const selectedHeaderTextColor = selectedColors?.selectedHeaderTextColor || headerTextColor;
  const translate = useLocalTranslation();
  const [toolTip, setToolTip] = useElement();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const leftPositions = ['bottom_left', 'top_left', 'center_left'];

  const addingFont = useCallback(() => {
    if (fontSet && fontSet.length) {
      const fontIndex = fontSet.findIndex((obj) => obj.fontId === selectedFontId);
      const styleNode = document.createElement('style');
      styleNode.type = 'text/css';
      const fontItem = fontSet[fontIndex];
      if (fontItem) {
        const styleText =
          '@font-face { font-family: "CustomFont"; font-style: normal; font-weight: 400; src: url(' +
          fontItem?.fontUrl +
          ');} \n';

        const styleTextNode = document.createTextNode(styleText);
        styleNode.appendChild(styleTextNode);
        document.head.appendChild(styleNode);
      }
    }
  }, [fontSet, selectedFontId]);

  useEffect(() => {
    addingFont();
    setCustomFontFamily(fontSet?.length ? `"CustomFont", "Open Sans", sans-serif` : '"Open Sans", sans-serif');
  }, [addingFont, fontSet]);

  const getContainerWidthAndHeight = () => {
    let width: number = 380;
    if (widgetDimension === 'Large') {
      width = 565;
    } else if (widgetDimension === 'Extra Large') {
      width = 1120;
    } else {
      width = 380;
    }
    const height = widgetDimension === 'Large' ? 943 : widgetDimension === 'Extra Large' ? 1822 : 650;
    return `${width} x ${height}`;
  };

  const width = enableMultiExperienceWidget ? (isMultiExpExpanded ? 968 : 420) : 380;

  const containerStyle = {
    width: `${width}px`,
    fontFamily: customFontFamily,
  };

  const collapseButtonStyle = {
    color: colorTheme,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    width: '100%',
    margin: 0,
  };

  const collapseFlexStyle = {
    borderRadius: '50%',
    backgroundColor: colorTheme,
    padding: '7px',
  };

  const updateIsMultiExpExpanded = () => {
    setIsMultiExpExpanded((prevVal) => !prevVal);
  };

  const handleChatToggleAlignment = useCallback(() => {
    let styleButton: any = {};
    styleButton = {
      ...styleButton,
      bottom: '16px',
      right: '20px',
      position: 'fixed',
    };
    const transformStyle = {
      transform:
        widgetButtonType === 'text' && textOrientationType === 'vertical' && !isExpanded
          ? widgetPosition && leftPositions.includes(widgetPosition)
            ? 'rotate(90deg)'
            : 'rotate(-90deg)'
          : 'rotate(0deg)',
    };
    styleButton = { ...styleButton, ...transformStyle };
    return styleButton;
  }, [widgetButtonType, textOrientationType, isExpanded, widgetPosition, leftPositions]);

  const expandedToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const [headerHeight, setHeaderHeight] = useState(64);

  const logoButtonStyle = {
    borderRadius: '8px 0px 0px 0px',
    color: headerTextColor,
    backgroundColor: colorTheme,
    padding: !enableKM ? '10px 4px 10px 4px' : '',
  };

  const headerHeightHandler = (height: number) => {
    return setHeaderHeight(height);
  };

  /**
   * marginRightForChat contains 24px margin and 4px extra to show at some distant
   */
  const marginRightForChat = 28;
  const expandedMultiExpWidgetWidth = 968;
  const collapsedMultiExpWidgetWidth = 420;
  const normalChatContainerWidth = 370;
  const marginRightForHelpIcon = isMultiExpExpanded
    ? expandedMultiExpWidgetWidth + marginRightForChat
    : enableMultiExperienceWidget
    ? collapsedMultiExpWidgetWidth + marginRightForChat
    : normalChatContainerWidth + marginRightForChat + 'px';

  /**
   * 24 represents the marginRightForChat, 31 represents the half of the dimension width to show in center
   */

  const dimensionRightValue = width / 2 + 24 - 31 + 'px';

  return (
    <>
      {isExpanded && (
        <>
          <Button
            variant="simple"
            icon
            style={{
              width: 20,
              height: 20,
              position: 'fixed',
              right: marginRightForHelpIcon,
              bottom: '634px',
              zIndex: 6001,
            }}
            ref={setToolTip}
          >
            <Icon name={'help'} />
            <Tooltip target={toolTip}>{translate('preview_note')} </Tooltip>
          </Button>
          <Card className="sample-chat-container" as={StyledCardPreview} style={containerStyle}>
            {enableMultiExperienceWidget ? (
              <Flex container={{ direction: 'row' }} style={{ height: '100%', width: '100%' }}>
                <Flex container={{ direction: 'column' }} style={{ borderRight: lightGray, width: '50px' }}>
                  <Flex>
                    <Button
                      variant="simple"
                      icon
                      style={{
                        ...logoButtonStyle,
                        height: `${headerHeight}px`,
                        width: '50px',
                        padding: 0,
                      }}
                      onClick={updateIsMultiExpExpanded}
                    >
                      {displayLogo && logo && (
                        <Image
                          src={logo}
                          alt="widget-logo"
                          style={{
                            height: 24,
                            width: 24,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Button>
                  </Flex>

                  {enableKM && (
                    <Button
                      variant="simple"
                      icon
                      style={{
                        marginLeft: '4px',
                        color: colorTheme,
                      }}
                      title="Help center"
                      as={IconButtonStyle}
                    >
                      <Icon name="help" />
                    </Button>
                  )}
                  {enableCases && (
                    <Button
                      variant="simple"
                      icon
                      style={{
                        marginLeft: '4px',
                        color: colorTheme,
                      }}
                      title="Services (Cases)"
                      as={IconButtonStyle}
                    >
                      <Icon name="wizard" />
                    </Button>
                  )}
                  <Button
                    variant="simple"
                    icon
                    style={{ marginLeft: '4px', color: colorTheme }}
                    title="Settings"
                    as={IconButtonStyle}
                  >
                    <Icon name="gear" />
                  </Button>
                  <Flex
                    style={{
                      height: '100%',
                      width: '100%',
                      margin: '0',
                    }}
                  ></Flex>
                  {isMultiExpExpanded && (
                    <Button
                      style={collapseButtonStyle}
                      as={IconButtonStyle}
                      variant="simple"
                      onClick={updateIsMultiExpExpanded}
                    >
                      <Flex style={collapseFlexStyle}>
                        <Icon
                          name={'play-solid'}
                          style={{ height: '10px', width: '10px', color: theme.base.colors.white }}
                        />
                      </Flex>
                    </Button>
                  )}
                </Flex>
                {isMultiExpExpanded && <MultiUserExperience />}
                <Flex container={{ direction: 'column' }} style={{ width: '370px' }}>
                  <ChatHeader
                    selectedThemeColor={selectedThemeColor}
                    selectedHeaderTextColor={selectedHeaderTextColor}
                    enableMultiExperienceWidget={enableMultiExperienceWidget}
                    headerHeightHandler={headerHeightHandler}
                  />
                  <Conversation selectedThemeColor={selectedThemeColor} agenticPreview={agenticPreview} />
                  <ConversationInput selectedThemeColor={selectedThemeColor} />
                </Flex>
              </Flex>
            ) : (
              <Flex container={{ direction: 'column' }} style={{ height: '100%' }}>
                <ChatHeader
                  selectedThemeColor={selectedThemeColor}
                  selectedHeaderTextColor={selectedHeaderTextColor}
                  enableMultiExperienceWidget={enableMultiExperienceWidget}
                  headerHeightHandler={headerHeightHandler}
                />
                <Conversation selectedThemeColor={selectedThemeColor} agenticPreview={agenticPreview} />
                <ConversationInput selectedThemeColor={selectedThemeColor} />
              </Flex>
            )}
            {footerText && <Footer />}
          </Card>
          {!enableMultiExperienceWidget && (
            <Flex as={StyledDimension} style={{ right: dimensionRightValue }}>
              {getContainerWidthAndHeight()}
            </Flex>
          )}
        </>
      )}
      {!hideChatLauncher && (
        <Flex style={handleChatToggleAlignment()}>
          <ChatToggle selectedColors={selectedColors} expandedToggle={expandedToggle} isExpanded={isExpanded} />
        </Flex>
      )}
    </>
  );
};

export default ChatContainer;
